import { IconButton, Option, Select } from '@mui/joy';

import { type AutocompleteOption } from '@/shared/hooks/autocomplete';

import FormField, { type FieldProps } from '../input/FormField';
import { ClearIcon } from '@/shared/icons/Icons';

type BaseProps<TOption extends AutocompleteOption> = {
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onBlur?: () => void;
  options: TOption[];
  value?: TOption['value'] | null | TOption['value'][];
  multiple?: boolean;
};

type DefaultProps<TOption extends AutocompleteOption> = BaseProps<TOption> & {
  clearable?: false;
  onChange: (value: TOption['value']) => void;
};

type ClearableProps<TOption extends AutocompleteOption> = BaseProps<TOption> & {
  clearable: true;
  onChange: (value: TOption['value'] | null) => void;
};

type SelectorProps<TOption extends AutocompleteOption> =
  | DefaultProps<TOption>
  | ClearableProps<TOption>;

type WithFieldProps<TOption extends AutocompleteOption> = SelectorProps<TOption> &
  FieldProps & {
    inputClassName?: string;
  };

export const BaseSelector = <TOption extends AutocompleteOption>({
  clearable,
  onChange,
  options,
  value,
  ...rest
}: SelectorProps<TOption>) => {
  const handleChange = (newValue: TOption['value'] | null) => {
    if (clearable) {
      onChange(newValue);
    } else if (newValue !== null) {
      onChange(newValue);
    }
  };

  const clearActionProps =
    clearable && value !== null
      ? {
          // display the button and remove select indicator
          // when user has selected a value
          endDecorator: (
            <IconButton variant="plain" color="neutral" onClick={() => onChange(null)}>
              <ClearIcon />
            </IconButton>
          ),
          indicator: null,
        }
      : null;

  return (
    <Select
      {...clearActionProps}
      value={value}
      onChange={(_, methodValue) => handleChange(methodValue)}
      {...rest}
    >
      {options.map((option) => (
        <Option key={option.value} value={option.value}>
          {option.label}
        </Option>
      ))}
    </Select>
  );
};

const Selector = <TOption extends AutocompleteOption>({
  className,
  error,
  horizontal,
  label,
  inputClassName,
  required,
  size,
  sx,
  translationKey,
  ...inputProps
}: WithFieldProps<TOption>) => (
  <FormField
    className={className}
    error={error}
    horizontal={horizontal}
    label={label}
    required={required}
    size={size}
    sx={sx}
    translationKey={translationKey}
  >
    <BaseSelector className={inputClassName} {...inputProps} />
  </FormField>
);

export default Selector;
